import { Link, NavLink, Outlet, useParams } from 'react-router-dom';
import type { Client } from '@/domain/schema/v1';
import { useLoad } from '../../hooks/useLoad';
import { useService } from '../../hooks/useService';

const TABS = [
  ['', '개요'],
  ['timeline', '흐름'],
  ['formulation', '사례개념화'],
  ['treatment', '치료 방향'],
  ['goals', '목표'],
  ['diagnosis', '진단적 고려'],
  ['assessments', '심리검사'],
  ['documents', '문서'],
  ['profile', '기본정보'],
] as const;

export type ClientOutlet = { client: Client; reloadClient: () => void };

/** 내담자 상세 공통 머리 (UX_FLOW §5) */
export function ClientShell() {
  const { clientId = '' } = useParams();
  const svc = useService();
  const { data: client, reload, error } = useLoad(() => svc.getClient(clientId), [svc, clientId]);
  if (error) return <p role="alert">내담자를 찾을 수 없습니다.</p>;
  if (!client) return <p className="muted">불러오는 중…</p>;
  return (
    <section className="stack">
      <div className="page-head client-head">
        <div>
          <h1 className="page-title">{client.clientCode}</h1>
          <p className="muted">
            {client.alias ? `${client.alias} · ` : ''}
            {client.totalSessions ? `${client.totalSessions}회기 진행` : '아직 회기 없음'} ·{' '}
            {{ active: '진행 중', paused: '보류', terminated: '종결' }[client.status]}
            {client.maxSessions ? ` · 최대 ${client.maxSessions}회` : ''}
          </p>
        </div>
        <div className="row client-actions">
          <Link className="button primary" to={`/clients/${client.id}/run`}>
            회기 진행 ▶
          </Link>
          <Link className="button" to={`/clients/${client.id}/brief`}>
            다음 회기 준비
          </Link>
          <Link className="small" to={`/sessions/new?clientId=${client.id}`}>
            새 회기 처리
          </Link>
        </div>
      </div>
      <nav className="tabs" aria-label="내담자 메뉴">
        {TABS.map(([path, label]) => (
          <NavLink
            key={path}
            to={`/clients/${client.id}${path ? `/${path}` : ''}`}
            end={!path}
            className={({ isActive }) => (isActive ? 'tab active' : 'tab')}
          >
            {label}
          </NavLink>
        ))}
      </nav>
      <Outlet context={{ client, reloadClient: reload } satisfies ClientOutlet} />
    </section>
  );
}
